import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { McButton, McIcon, McTag, McSegmentedControl, McSegmentedControlItem } from '@maersk-global/mds-react-wrapper';
import { tasksApi } from '../api/tasksApi';
import { plansApi } from '../api/plansApi';
import { LoadingIndicator } from './LoadingIndicator';
import { EditableText } from './EditableText';
import { LlmLogPanel } from './LlmLogPanel';
import { ExecutionPlanView } from './ExecutionPlanView';
import { TaskDescriptionView } from './TaskDescriptionView';
import { Breadcrumb } from './Breadcrumb';
import {
  taskExecutionStatusAppearance,
  formatTaskExecutionStatus,
  planStatusAppearance,
  formatPlanType,
  formatCost,
} from '../utils/helpers/statusHelpers';
import type { TaskExecutionStatus, TaskPlanDetail } from '../types/engineering';

export interface TaskDetailContentProps {
  id: string;
  /** Called when back is triggered (modal context). If omitted, uses URL navigation. */
  onBack?: () => void;
}

const VULNERABILITY_STATUSES: TaskExecutionStatus[] = [
  'SCANNING_VULNERABILITIES',
  'SCANNING_VULNERABILITIES_ERROR',
  'TESTING',
  'TEST_ERROR',
  'DONE',
  'ERROR',
  'REJECTED',
];

export const TaskDetailContent = ({ id, onBack }: TaskDetailContentProps) => {
  const queryClient = useQueryClient();
  const [approvingTask, setApprovingTask] = useState(false);
  const [approvingPlanId, setApprovingPlanId] = useState<string | null>(null);
  const [retryingPlanner, setRetryingPlanner] = useState(false);
  const [retryingImplementer, setRetryingImplementer] = useState(false);
  const [retryingVulnerabilityScan, setRetryingVulnerabilityScan] = useState(false);
  const [showLlmLogs, setShowLlmLogs] = useState(false);
  const [showVulnLogs, setShowVulnLogs] = useState(false);
  const [showImplementerLogs, setShowImplementerLogs] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'plan' | 'vulnerabilities'>('plan');
  const [showRetryScanConfirm, setShowRetryScanConfirm] = useState(false);

  const { data: task, isLoading, isError } = useQuery({
    queryKey: ['tasks', id],
    queryFn: () => tasksApi.getById(id),
    enabled: !!id,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['plans', 'task', id],
    queryFn: () => plansApi.listByTask(id),
    enabled: !!id,
  });

  // Modal context: onBack provided by parent (e.g., KanbanModal)
  // URL context: onBack is undefined, use breadcrumb with route links
  const isModalContext = !!onBack;

  const handleTaskApprove = async (approved: boolean) => {
    setApprovingTask(true);
    try {
      await tasksApi.approve(id, approved);
      await queryClient.refetchQueries({ queryKey: ['tasks', id] });
      await queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
      // Auto-open Planner logs panel when task is approved
      if (approved) {
        setShowLlmLogs(true);
      }
    } finally {
      setApprovingTask(false);
    }
  };

  const handleTaskDescriptionSave = async (description: string) => {
    await tasksApi.updateDescription(id, description);
    await queryClient.refetchQueries({ queryKey: ['tasks', id] });
  };

  const handlePlanApprove = async (planId: string, approved: boolean) => {
    setApprovingPlanId(planId);
    try {
      await plansApi.approve(planId, approved);
      await queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
      await queryClient.refetchQueries({ queryKey: ['tasks', id] });
      // Auto-open Implementer logs panel when plan is approved
      if (approved) {
        setShowImplementerLogs((prev) => ({ ...prev, [planId]: true }));
      }
    } finally {
      setApprovingPlanId(null);
    }
  };

  const handlePlanDescriptionSave = async (planId: string, executionPlan: string) => {
    await plansApi.updateDescription(planId, executionPlan);
    await queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
  };

  const handleRetryPlanner = async () => {
    setRetryingPlanner(true);
    try {
      await tasksApi.retryPlanner(id);
      await queryClient.refetchQueries({ queryKey: ['tasks', id] });
      await queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
      // Auto-open Planner logs panel after retry
      setShowLlmLogs(true);
    } finally {
      setRetryingPlanner(false);
    }
  };

  const handleRetryImplementer = async () => {
    setRetryingImplementer(true);
    try {
      await tasksApi.retryImplementer(id);
      await queryClient.refetchQueries({ queryKey: ['tasks', id] });
      await queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
      // Auto-open Implementer logs panel for the retried plan
      // The backend changes the FAILED plan to WAITING_IMPLEMENTATION status
      // Wait a moment for React Query to refetch, then find and open that plan's logs
      setTimeout(() => {
        const updatedPlans = queryClient.getQueryData<TaskPlanDetail[]>(['plans', 'task', id]);
        const retriedPlan = updatedPlans?.find(p => p.status === 'WAITING_IMPLEMENTATION');
        if (retriedPlan) {
          setShowImplementerLogs((prev) => ({ ...prev, [retriedPlan.id]: true }));
        }
      }, 500);
    } finally {
      setRetryingImplementer(false);
    }
  };

  const handleRetryVulnerabilityScan = async () => {
    setShowRetryScanConfirm(false);
    setRetryingVulnerabilityScan(true);
    try {
      await tasksApi.retryVulnerabilityScan(id);
      await queryClient.refetchQueries({ queryKey: ['tasks', id] });
      await queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
    } catch (err) {
      console.error('[RETRY SCAN] Failed to retry vulnerability scan:', err);
    } finally {
      setRetryingVulnerabilityScan(false);
    }
  };

  if (isLoading) return <LoadingIndicator />;

  if (isError || !task) {
    return (
      <div>
        <Breadcrumb
          items={
            isModalContext
              ? [
                  { label: 'Demand Detail', onClick: onBack },
                  { label: 'Task Detail' }
                ]
              : [
                  { label: 'Demands', path: '/demands' },
                  { label: 'Demand Detail' },
                  { label: 'Task Detail' }
                ]
          }
        />
        <div className="empty-state">Task not found.</div>
      </div>
    );
  }

  const insertDateFormatted = task.insertDate
    ? new Date(task.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const plannerPlans = (plans as TaskPlanDetail[]).filter(
    (plan) => !plan.planType || plan.planType === 'PLANNER'
  );

  const vulnPlans = (plans as TaskPlanDetail[]).filter(
    (plan) => plan.planType === 'SCAN_VULNERABILITIES'
  );

  // Show vulnerabilities tab if task is in vulnerability-related status OR if there are vulnerability plans
  const showVulnerabilitiesTab = VULNERABILITY_STATUSES.includes(task.executionStatus) || vulnPlans.length > 0;

  return (
    <div>
      {/* Context-aware breadcrumb: 2 levels in modal, 3 levels in URL navigation */}
      <Breadcrumb
        items={
          isModalContext
            ? [
                { label: 'Demand Detail', onClick: onBack },
                { label: 'Task Detail' }
              ]
            : [
                { label: 'Demands', path: '/demands' },
                { label: 'Demand Detail', path: `/demands/${task.demandId}` },
                { label: 'Task Detail' }
              ]
        }
      />

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--mds_brand_appearance_neutral_default_text-color)', textTransform: 'uppercase' }}>
              {task.code}
            </span>
            <McTag label={formatTaskExecutionStatus(task.executionStatus)} appearance={taskExecutionStatusAppearance(task.executionStatus)} fit="small" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {task.executionStatus === 'WAITING_REVIEW' && (
              <>
                <McButton label="Approve" appearance="primary" fit="small" disabled={approvingTask} click={() => handleTaskApprove(true)} />
                <McButton label="Reject" appearance="error" fit="small" disabled={approvingTask} click={() => handleTaskApprove(false)} />
              </>
            )}
            {task.executionStatus === 'PLANNING_ERROR' && (
              <McButton
                label="Retry Planner"
                appearance="primary"
                fit="small"
                loading={retryingPlanner}
                title="Retry creating a plan for this task"
                click={handleRetryPlanner}
              />
            )}
            {task.executionStatus === 'IMPLEMENTING_ERROR' && (
              <McButton
                label="Retry Implementer"
                appearance="primary"
                fit="small"
                loading={retryingImplementer}
                title="Retry implementing this task"
                click={handleRetryImplementer}
              />
            )}
            {(task.executionStatus === 'SCANNING_VULNERABILITIES' || task.executionStatus === 'SCANNING_VULNERABILITIES_ERROR') && (
              <McButton
                label="Retry Scan Vulnerabilities"
                appearance="primary"
                fit="small"
                loading={retryingVulnerabilityScan}
                click={() => setShowRetryScanConfirm(true)}
              />
            )}
          </div>
        </div>
        <h1 style={{ margin: 0 }}>{task.title}</h1>
      </div>

      {/* Retry Scan Vulnerabilities Confirmation Dialog */}
      {showRetryScanConfirm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--mds_brand_appearance_neutral_default_background-color, #fff)',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
          }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: 600 }}>Confirm Retry</h2>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--mds_brand_appearance_neutral_default_text-color)' }}>
              After confirmation, we will send a new Redis message to reprocess this task. Are you sure?
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <McButton label="Cancel" appearance="neutral" fit="small" click={() => setShowRetryScanConfirm(false)} />
              <McButton label="Confirm" appearance="primary" fit="small" click={handleRetryVulnerabilityScan} />
            </div>
          </div>
        </div>
      )}

      <div className="demand-detail-body">
        <div className="demand-detail-body__left">
          {task.description && (
            <div className="detail-section">
              <h2 className="detail-section__title">Description</h2>
              <EditableText value={task.description} onSave={handleTaskDescriptionSave} label="Task Description" renderDisplay={TaskDescriptionView} />
            </div>
          )}

          <div className="detail-section">
            {/* Tab 1: Plan */}
            {activeTab === 'plan' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem' }}>
                  {/* Tab navigation or title */}
                  {showVulnerabilitiesTab ? (
                    <McSegmentedControl
                      value={activeTab}
                      listchange={(e: any) => {
                        const newTab = e.detail?.item?.value;
                        if (newTab && (newTab === 'plan' || newTab === 'vulnerabilities')) {
                          setActiveTab(newTab);
                        }
                      }}
                    >
                      <McSegmentedControlItem value="plan" label="Plan" />
                      <McSegmentedControlItem value="vulnerabilities" label="Vulnerabilities" />
                    </McSegmentedControl>
                  ) : (
                    <h2 className="detail-section__title" style={{ margin: 0 }}>Plans ({plannerPlans.length})</h2>
                  )}
                  
                  {/* LLM Logs button */}
                  {task.executionStatus !== 'WAITING_REVIEW' && task.executionStatus !== 'REJECTED' && (
                    <McButton label={`Plans (${plannerPlans.length})`} appearance="neutral" fit="small" click={() => setShowLlmLogs((v) => !v)}>
                      <McIcon icon="robot" slot="icon" />
                    </McButton>
                  )}
                </div>

                {showLlmLogs && (
                  <LlmLogPanel
                    executionId={task.id}
                    agent="PLANNER"
                    onDone={() => {
                      console.log('[PLANNER DONE] Refetching queries...');
                      queryClient.refetchQueries({ queryKey: ['tasks', id] });
                      queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
                      // Invalidate parent demand to update cost/progress in lists
                      if (task?.demandId) {
                        queryClient.refetchQueries({ queryKey: ['demands', task.demandId] });
                      }
                      console.log('[PLANNER DONE] Queries refetched successfully');
                    }}
                  />
                )}

                {plannerPlans.length === 0 ? (
                  <div className="empty-state">No plans yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {plannerPlans.map((plan) => (
                      <div key={plan.id} className="plan-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div className="plan-card__left">
                            {/* <div className="plan-card__code">{plan.code}</div> */}
                            {/* <div className="plan-card__title">{plan.title}</div> */}
                            <div className="plan-card__type">{formatPlanType(plan.type)}</div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0 }}>
                            {plan.cost != null && (
                              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--mds_brand_appearance_neutral_weak_text-color)', whiteSpace: 'nowrap' }}>
                                {formatCost(plan.cost)}
                              </span>
                            )}
                            <McTag label={plan.status} appearance={planStatusAppearance(plan.status)} fit="small" />
                            {plan.status === 'WAITING_REVIEW' && (
                              <>
                                <McButton label="Approve" appearance="primary" fit="small" disabled={approvingPlanId === plan.id} click={() => handlePlanApprove(plan.id, true)} />
                                <McButton label="Reject" appearance="error" fit="small" disabled={approvingPlanId === plan.id} click={() => handlePlanApprove(plan.id, false)} />
                              </>
                            )}

                            {plan.status !== 'WAITING_REVIEW' && (
                              <McButton
                                label="Implementer"
                                appearance="neutral"
                                fit="small"
                                click={() => setShowImplementerLogs((prev) => ({ ...prev, [plan.id]: !prev[plan.id] }))}
                              >
                                <McIcon icon="robot" slot="icon" />
                              </McButton>
                            )}
                          </div>
                        </div>
                        {showImplementerLogs[plan.id] && (
                          <LlmLogPanel
                            executionId={plan.id}
                            agent="IMPLEMENTER"
                            onDone={() => {
                              queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
                              queryClient.refetchQueries({ queryKey: ['tasks', id] });
                              // Invalidate parent demand to update cost/progress in lists
                              if (task?.demandId) {
                                queryClient.refetchQueries({ queryKey: ['demands', task.demandId] });
                              }
                            }}
                          />
                        )}
                        <div style={{ marginTop: '0.75rem' }}>
                          <EditableText
                            value={plan.executionPlan ?? ''}
                            onSave={(executionPlan) => handlePlanDescriptionSave(plan.id, executionPlan)}
                            label="Execution Plan"
                            renderDisplay={ExecutionPlanView}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Tab 2: Vulnerabilities */}
            {activeTab === 'vulnerabilities' && showVulnerabilitiesTab && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem' }}>
                  {/* Tab navigation */}
                  <McSegmentedControl
                    value={activeTab}
                    listchange={(e: any) => {
                      const newTab = e.detail?.item?.value;
                      if (newTab && (newTab === 'plan' || newTab === 'vulnerabilities')) {
                        setActiveTab(newTab);
                      }
                    }}
                  >
                    <McSegmentedControlItem value="plan" label="Plan" />
                    <McSegmentedControlItem value="vulnerabilities" label="Vulnerabilities" />
                  </McSegmentedControl>
                  
                  {/* LLM Logs button */}
                  <McButton label={`Vulnerabilities (${vulnPlans.length})`} appearance="neutral" fit="small" click={() => setShowVulnLogs((v) => !v)}>
                    <McIcon icon="robot" slot="icon" />
                  </McButton>
                </div>

                {showVulnLogs && (
                  <LlmLogPanel
                    executionId={task.id}
                    agent="VULNERABILITIES"
                    onDone={() => {
                      queryClient.refetchQueries({ queryKey: ['tasks', id] });
                      queryClient.refetchQueries({ queryKey: ['plans', 'task', id] });
                      if (task?.demandId) {
                        queryClient.refetchQueries({ queryKey: ['demands', task.demandId] });
                      }
                    }}
                  />
                )}

                {vulnPlans.length === 0 ? (
                  <div className="empty-state">No vulnerability scan results yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {vulnPlans.map((plan) => (
                      <div key={plan.id} className="plan-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div className="plan-card__left">
                            <div className="plan-card__type">Vulnerability Report</div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0 }}>
                            {plan.cost != null && (
                              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--mds_brand_appearance_neutral_weak_text-color)', whiteSpace: 'nowrap' }}>
                                {formatCost(plan.cost)}
                              </span>
                            )}
                            <McTag label={plan.status} appearance={planStatusAppearance(plan.status)} fit="small" />
                          </div>
                        </div>
                        <div style={{ marginTop: '0.75rem' }}>
                          {ExecutionPlanView(plan.executionPlan ?? '')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="demand-detail-body__right">
          <div className="detail-section">
            <h2 className="detail-section__title">Details</h2>
            <div className="detail-info-grid">
              <div>
                <div className="detail-info-item__label">Created at</div>
                <div className="detail-info-item__value">{insertDateFormatted}</div>
              </div>
              {task.repository && (
                <div>
                  <div className="detail-info-item__label">Repository</div>
                  <div className="detail-info-item__value" style={{ wordBreak: 'break-all' }}>{task.repository}</div>
                </div>
              )}
              {task.cost != null && (
                <div>
                  <div className="detail-info-item__label">Cost</div>
                  <div className="detail-info-item__value" style={{ fontWeight: 600 }}>{formatCost(task.cost)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
